import type { Patient } from "@src/packages/patients/patients.types";
import { isCurrentPartnerParticipation } from "@src/packages/experience";
import type { CycleType } from "./hooks/useCycles";

/** Cycle types that represent a shared treatment rather than a solo journey. */
const SHARED_CYCLE_TYPES: ReadonlySet<string> = new Set([
  "ivf",
  "icsi",
  "iui",
  "fet",
  "embryo_freezing",
]);

/**
 * The partner Care boundary is the server-resolved relationship, not sex at
 * birth or the presence of a legacy partner ID. `partnerConnected` confirms
 * that the relationship is active from the viewer's perspective.
 */
export function isCurrentPartnerCareViewer(
  patient: Patient | null | undefined,
): boolean {
  return Boolean(
    patient?.partnerId &&
    patient.partnerConnected === true &&
    isCurrentPartnerParticipation(
      patient.participantRole,
      patient.participantStatus,
    ),
  );
}

export function isSharedCycleType(
  cycleType: CycleType | string | null | undefined,
): cycleType is CycleType {
  return (
    cycleType !== null &&
    cycleType !== undefined &&
    SHARED_CYCLE_TYPES.has(cycleType)
  );
}

export function shouldUsePartnerCyclePerspective(args: {
  patient: Patient | null | undefined;
  cycleType: CycleType | string | null | undefined;
}): boolean {
  return (
    isCurrentPartnerCareViewer(args.patient) &&
    isSharedCycleType(args.cycleType)
  );
}

/**
 * Resolve the copy variant to render for a shared-treatment step.
 *
 * Distinct from `shouldUsePartnerCyclePerspective`, which asks a role question
 * ("am I a linked partner viewing my partner's cycle record?") and gates
 * write access on read-only surfaces. This asks an anatomy question: "does
 * the canonical self copy — which assumes AFAB anatomy — describe the
 * viewer?"
 *
 * Returns "partner" when either:
 * - the viewer is a linked partner reading a shared cycle (existing case), or
 * - the viewer is AMAB in a shared cycle (IVF/ICSI/IUI/FET/embryo_freezing).
 *   Even without a linked partner, and even when the AMAB viewer owns the
 *   record, self copy that says "your eggs" / "your uterus" is anatomically
 *   wrong. The partner-copy layer already reads correctly for this viewer.
 */
export function resolveStepCopyPerspective(args: {
  patient: Patient | null | undefined;
  cycleType: CycleType | string | null | undefined;
}): "self" | "partner" {
  if (shouldUsePartnerCyclePerspective(args)) return "partner";
  if (args.patient?.gender === "AMAB" && isSharedCycleType(args.cycleType)) {
    return "partner";
  }
  return "self";
}
